import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';

import API from '../../utils/axios';
import { authSliceActions } from './authSlice';

export interface adminStateType {
	users: object[];
	resetpasswordbyAdminData: null | object;
	updateuserData: null | object;
	getuserData: null | object;
	exportUserData: null | object;
	importUserData: null | object | string;
	addUserData: null | object;
	totalCount: null | Number;
	paginationOptions: object;
	searchUserData: null | object[];
	errorMessage: string;
}

const initialState: adminStateType = {
	users: [],
	resetpasswordbyAdminData: null,
	updateuserData: null,
	getuserData: null,
	exportUserData: null,
	importUserData: null,
	addUserData: null,
	totalCount: 0,
	paginationOptions: {
		page: 0,
		rows: 10,
	},
	searchUserData: null,
	errorMessage: '',
};

//api request to get all users data
export const getusers = createAsyncThunk(
	'admin/getusers',
	async (args: any, thunkApi) => {
		const { navigate, page, rowsPerPage, deferredValue, order, orderBy } = args;
		try {
			const res = await API.post(`/admin/getusers`, {
				page,
				rows: rowsPerPage,
				searchStr: deferredValue,
				field: orderBy,
				type: order,
			});

			return res.data;
		} catch (err) {
			const error = err as AxiosError;
			error?.response?.status === 403 && navigate('/');
			error?.response?.status === 403 &&
				thunkApi.dispatch(authSliceActions.signout());
			return thunkApi.rejectWithValue(error);
		}
	}
);

export const searchUsers = createAsyncThunk(
	'admin/searchUsers',
	async (args: any, thunkApi) => {
		const { page, rowsPerPage, deferredValue } = args;
		try {
			const res = await API.get(
				`/admin/searchusers?page=${page}&rows=${rowsPerPage}&searchStr=${deferredValue}`
			);

			return res.data;
		} catch (error) {
			console.log(error);
		}
	}
);

//api request to reset password by admin
export const resetpasswordbyadmin = createAsyncThunk(
	'admin/resetpasswordbyadmin',
	async (args: any, thunkApi) => {
		const { newData, navigate } = args;
		try {
			thunkApi.dispatch(authSliceActions.start_loading());
			const res = await API.patch(
				`/admin/resetpassword/${newData.id}`,
				newData
			);
			thunkApi.dispatch(authSliceActions.end_loading());
			res.data?._id && toast.success('Password changed successfully.');
			return res.data;
		} catch (err) {
			const error = err as AxiosError;
			if (error?.response?.status === 403) {
				navigate('/');
				thunkApi.dispatch(authSliceActions.signout());
			}
			thunkApi.dispatch(authSliceActions.end_loading());
			return thunkApi.rejectWithValue(error);
		}
	}
);

export const deleteUserApi = createAsyncThunk('/user/deleteUserApi', async (args: any, thunkApi) => {
	const { id } = args;
	try {
		const res = await API.get(`/user/deleteUser/${id}`);
		return res.data;
	} catch (error) {
		return thunkApi.rejectWithValue(error);
	}
});

//Api request to update user details by admin
export const updateuser = createAsyncThunk(
	'admin/updateuser',
	async (args: any, thunkApi) => {
		const { newData, navigate, page, rowsPerPage } = args;
		try {
			thunkApi.dispatch(authSliceActions.start_loading());
			const res = await API.put(`/admin/updateuser/${newData._id}`, newData);
			thunkApi.dispatch(authSliceActions.end_loading());
			thunkApi.dispatch(
				getusers({ page, rowsPerPage, deferredValue: '', navigate })
			);

			res.data?._id
				? toast.success('User updated successfully')
				: toast.error(res.data.message);

			return res.data;
		} catch (err) {
			const error = err as AxiosError;
			if (error?.response?.status === 403) {
				navigate('/');
				thunkApi.dispatch(authSliceActions.signout());
			}
			thunkApi.dispatch(authSliceActions.end_loading());
			return thunkApi.rejectWithValue(error);
		}
	}
);

//Api request for create new user through admin
export const addUserByAdmin = createAsyncThunk(
	'admin/addUserByAdmin',
	async (args: any, thunkApi) => {
		const { newData, navigate, page, rowsPerPage } = args;
		try {
			thunkApi.dispatch(authSliceActions.start_loading());
			const res = await API.post('/admin/adduser', newData);
			thunkApi.dispatch(authSliceActions.end_loading());
			thunkApi.dispatch(
				getusers({ page, rowsPerPage, deferredValue: '', navigate })
			);
			thunkApi.dispatch(
				adminSliceActions.updatePaginationOptions({
					page: 0,
					rows: rowsPerPage,
				})
			);
			res.data?.success
				? toast.success('User added successfully')
				: toast.error(res.data?.message);

			return res.data;
		} catch (err) {
			const error = err as AxiosError;
			if (error?.response?.status === 403) {
				navigate('/');
				thunkApi.dispatch(authSliceActions.signout());
			}
			thunkApi.dispatch(authSliceActions.end_loading());
			return thunkApi.rejectWithValue(error);
		}
	}
);

//Api request to get particular user data for admin
export const getuser = createAsyncThunk(
	'admin/getuser',
	async (args: any, thunkApi) => {
		const { state, navigate } = args;
		try {
			thunkApi.dispatch(authSliceActions.start_loading());
			const res = await API.get(`/admin/getuser/${state}`);
			thunkApi.dispatch(authSliceActions.end_loading());
			return res.data;
		} catch (err) {
			const error = err as AxiosError;
			if (error?.response?.status === 403) {
				navigate('/');
				thunkApi.dispatch(authSliceActions.signout());
			}
			thunkApi.dispatch(authSliceActions.end_loading());
			return thunkApi.rejectWithValue(error);
		}
	}
);

//Api request for export user by admin
export const exportUserApi = createAsyncThunk(
	'admin/exportUserApi',
	async (navigate: any, thunkApi) => {
		try {
			thunkApi.dispatch(authSliceActions.start_loading());
			const res = await API.get('/admin/exportusers');
			thunkApi.dispatch(authSliceActions.end_loading());
			return res.data;
		} catch (err) {
			const error = err as AxiosError;
			if (error?.response?.status === 403) {
				navigate('/');
				thunkApi.dispatch(authSliceActions.signout());
			}
			thunkApi.dispatch(authSliceActions.end_loading());
			return thunkApi.rejectWithValue(error);
		}
	}
);

//Api request for importing user from excelfiles
export const importUserApi = createAsyncThunk(
	'admin/importUserApi',
	async (args: any, thunkApi) => {
		const { formData, navigate } = args;
		const config = {
			headers: {
				'content-type': 'multipart/form-data',
			},
		};
		try {
			thunkApi.dispatch(authSliceActions.start_loading());
			const res = await API.post('/admin/importexcel', formData, config);
			res.data?.success
				? toast.success(res.data?.message)
				: toast.error(res.data?.message);
			thunkApi.dispatch(authSliceActions.end_loading());
			return res.data;
		} catch (err) {
			const error: any = err as AxiosError;
			error?.response?.data?.success ||
				toast.error(error?.response?.data?.message);

			if (error?.response?.status === 403) {
				navigate('/');
				thunkApi.dispatch(authSliceActions.signout());
			}
			thunkApi.dispatch(authSliceActions.end_loading());
			return thunkApi.rejectWithValue(error);
		}
	}
);

//Api request for table sorting
export const sortingTableApi = createAsyncThunk(
	'/admin/sortingTableApi',
	async (args: any, thunkApi) => {
		const { data, navigate } = args;
		try {
			const res = await API.post('/admin/sortUsers', data);

			return res.data;
		} catch (err) {
			const error = err as AxiosError;
			if (error?.response?.status === 403) {
				navigate('/');
				thunkApi.dispatch(authSliceActions.signout());
			}
			thunkApi.dispatch(authSliceActions.end_loading());
			return thunkApi.rejectWithValue(error);
		}
	}
);

//AdminSlice
const adminSlice = createSlice({
	name: 'admin',
	initialState,
	extraReducers: (builder) => {
		builder.addCase(getusers.fulfilled, (state, action) => {
			state.users = action?.payload?.result;
			state.totalCount = action?.payload?.totalCount;
		});

		builder.addCase(getuser.pending, () => { });

		builder.addCase(getusers.rejected, (state: adminStateType, action: any) => {
			state.users = action.payload;
		});

		builder.addCase(resetpasswordbyadmin.fulfilled, (state, action) => {
			state.resetpasswordbyAdminData = action?.payload;
		});

		builder.addCase(resetpasswordbyadmin.pending, () => { });

		builder.addCase(
			resetpasswordbyadmin.rejected,
			(state: adminStateType, action) => {
				state.resetpasswordbyAdminData = action?.error;
			}
		);

		builder.addCase(updateuser.fulfilled, (state: adminStateType, action) => {
			state.updateuserData = action?.payload;
			state.getuserData = action?.payload;
			state.users = state.users.map((item: any) => {
				return item._id === action?.payload?._id ? action?.payload : item;
			});
		});

		builder.addCase(updateuser.pending, (state: adminStateType) => { });

		builder.addCase(updateuser.rejected, (state: adminStateType, action) => {
			state.updateuserData = action?.error;
		});

		builder.addCase(
			addUserByAdmin.fulfilled,
			(state: adminStateType, action) => {
				state.addUserData = action?.payload?.result?.user;
			}
		);

		builder.addCase(addUserByAdmin.pending, (state: adminStateType) => { });

		builder.addCase(
			addUserByAdmin.rejected,
			(state: adminStateType, action) => {
				state.updateuserData = action?.error;
			}
		);

		builder.addCase(getuser.fulfilled, (state, action) => {
			state.getuserData = action?.payload;
		});

		builder.addCase(getuser.rejected, (state: any, action) => {
			state.getuserData = action?.payload;
		});
		builder.addCase(exportUserApi.fulfilled, (state, action) => {
			state.exportUserData = action?.payload;
		});
		builder.addCase(exportUserApi.rejected, (state: any, action) => {
			state.exportUserData = action?.payload;
		});
		builder.addCase(
			importUserApi.fulfilled,
			(state: adminStateType, action) => {
				state.importUserData = 'Users Added Successfully';
				state.getuserData = action?.payload?.result;
				state.totalCount = action?.payload?.totalCount;
			}
		);
		builder.addCase(importUserApi.rejected, (state: any, action) => {
			state.importUserData = action?.payload;
		});

		builder.addCase(searchUsers.fulfilled, (state, action) => {
			state.users = action?.payload?.result;
			state.totalCount = action?.payload?.totalCount;
		});
		builder.addCase(searchUsers.rejected, (state, action) => {
			state.errorMessage = String(action?.error);
		});

		builder.addCase(sortingTableApi.fulfilled, (state, action) => {
			state.users = action?.payload;
		});

		builder.addCase(sortingTableApi.rejected, (state, action) => {
			state.errorMessage = String(action.error);
		});
	},
	reducers: {
		emptyState: (state) => {
			state.updateuserData = null;
			state.getuserData = null;
		},
		emptyUserList: (state) => {
			state.users = [];
		},
		emptyExportUserData: (state) => {
			state.exportUserData = null;
		},
		emptyResetPasswordData: (state) => {
			state.resetpasswordbyAdminData = null;
		},
		updatePaginationOptions: (state, action) => {
			state.paginationOptions = action?.payload;
		},
	},
});

export default adminSlice;
export const adminSliceActions = adminSlice.actions;
